"use client";

import { useLayoutEffect, useRef, useState } from "react";

import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * One line of text that is cut with an ellipsis when it does not fit, with the
 * full text in a tooltip on hover. The tooltip exists only while the text is
 * actually clipped, so a short name carries no extra element and nothing to
 * tab through, and it is a plain span rather than a button so it can sit
 * inside a card that is itself a link. Use it wherever a name or email may run
 * long in a row or card; a block of copy that should wrap is not this.
 *
 * `full` is the whole text when `text` is already a shortened form of it — a
 * live line trimmed to its last words, say, where CSS can only clip the end.
 * `nativeTitle` swaps the tooltip for the browser's own `title`, for a surface
 * a Radix portal cannot reach: the Guide floating window is its own document,
 * so a tooltip portalled into the opener would render in the wrong window.
 * That title is always set, since such a window cannot be measured reliably.
 */
export default function TruncatedText({
  text,
  full,
  className,
  as: Tag = "span",
  nativeTitle = false,
  focusWhenClipped = false,
  ...rest
}) {
  const ref = useRef(null);
  const [clipped, setClipped] = useState(false);
  const whole = full ?? text;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setClipped(el.scrollWidth > el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  const line = (
    <Tag
      ref={ref}
      {...rest}
      {...(focusWhenClipped ? { tabIndex: clipped ? 0 : undefined } : null)}
      className={cn("block min-w-0 truncate", className)}
      {...(nativeTitle ? { title: whole } : null)}
    >
      {text}
    </Tag>
  );

  if (nativeTitle || !clipped) return line;

  return <SimpleTooltip content={whole}>{line}</SimpleTooltip>;
}
